import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../../config/apiConfig';
import { normalizeFileName } from '../../../components/ui/utils/fileNameEncoding';
import {
  fetchRiskAssessmentReferenceDocument,
  formatRiskAssessmentReferenceDocumentText,
} from '../utils/fetchRiskAssessmentReferenceDocument';

/**
 * RiskAssessmentReport - Printable report component for risk assessments
 * Displays all risk assessment information in a print-friendly format
 */
const RiskAssessmentReport = ({ riskAssessment }) => {
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoFetchAttempted, setLogoFetchAttempted] = useState(false);
  const [referenceDocument, setReferenceDocument] = useState(null);
  const [referenceDocLoading, setReferenceDocLoading] = useState(true);

  // Fetch company logo from application settings
  useEffect(() => {
    if (logoFetchAttempted) return;
    
    const fetchCompanyLogo = async () => {
      setLogoFetchAttempted(true);
      
      try {
        const token = localStorage.getItem('authToken');
        const url = `${API_BASE_URL}/application-setting/key/companyLogo`;
        
        const fetchOptions = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        };
        
        const response = await fetch(url, fetchOptions);

        if (response.ok) {
          const setting = await response.json();
          
          if (setting && setting.value && setting.isActive !== false) {
            let logoUrl = setting.value.trim();
            
            if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
              const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
              logoUrl = logoUrl.startsWith('/') 
                ? `${baseUrl}${logoUrl}` 
                : `${baseUrl}/${logoUrl}`;
            }
            
            setCompanyLogo(logoUrl);
          }
        }
      } catch (error) {
        console.error('Error fetching company logo:', error);
        // Logo is optional, so we don't show error to user
      }
    };

    fetchCompanyLogo();
  }, [logoFetchAttempted]);

  useEffect(() => {
    let cancelled = false;

    const loadReferenceDocument = async () => {
      if (riskAssessment?.referenceDocument) {
        setReferenceDocument({ snapshotText: riskAssessment.referenceDocument });
        setReferenceDocLoading(false);
        return;
      }

      setReferenceDocLoading(true);
      try {
        const doc = await fetchRiskAssessmentReferenceDocument();
        if (!cancelled) setReferenceDocument(doc);
      } catch {
        if (!cancelled) setReferenceDocument(null);
      } finally {
        if (!cancelled) setReferenceDocLoading(false);
      }
    };

    loadReferenceDocument();
    return () => {
      cancelled = true;
    };
  }, [riskAssessment?.id, riskAssessment?.referenceDocument]);

  if (!riskAssessment) {
    return <div>No risk assessment data available</div>;
  }

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return dayjs(date).format('DD/MM/YYYY HH:mm');
    } catch {
      return date;
    }
  };

  // Helper function to format date only
  const formatDateOnly = (date) => {
    if (!date) return 'N/A';
    try {
      return dayjs(date).format('DD/MM/YYYY');
    } catch {
      return date;
    }
  };

  // Helper function to display array of objects
  const formatArray = (arr, field = 'name') => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return 'N/A';
    return arr.map(item => {
      if (typeof item === 'object') {
        return item.userDisplayName || item.name || item.title || item.label || 
               (item.userFirstName && item.userLastName ? `${item.userFirstName} ${item.userLastName}` : 'N/A');
      }
      return item;
    }).join(', ');
  };

  return (
    <div className="risk-assessment-report print-only" style={styles.container}>
      {/* Print Styles */}
      <style>{printStyles}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            {companyLogo ? (
              <img 
                src={companyLogo} 
                alt="Company Logo" 
                style={styles.logo}
                onError={(e) => {
                  console.error('❌ Failed to load logo image:', companyLogo, e);
                  e.target.style.display = 'none';
                }}
                onLoad={() => {
                  console.log('✅ Logo loaded successfully:', companyLogo);
                }}
              />
            ) : (
              <div style={{ fontSize: '10px', color: '#999', fontStyle: 'italic' }}>
                {process.env.NODE_ENV === 'development' && 'No logo configured'}
              </div>
            )}
          </div>
          <div style={styles.headerRight}>
            <h1 style={styles.title}>RISK ASSESSMENT REPORT</h1>
            <div style={styles.referenceDocument}>
              {referenceDocLoading ? (
                <span style={styles.referenceDocumentMuted}>Loading…</span>
              ) : (
                referenceDocument?.snapshotText ||
                formatRiskAssessmentReferenceDocumentText(referenceDocument) || (
                  <span style={styles.referenceDocumentMuted}>N/A</span>
                )
              )}
            </div>
            <div style={styles.headerInfo}>
              <div style={styles.reportMeta}>
                <span>Generated: {dayjs().format('DD/MM/YYYY HH:mm')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Basic Information Section */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>1. BASIC INFORMATION</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Risk Assessment Code:</td>
              <td style={styles.valueCell}><strong>{riskAssessment.code || 'N/A'}</strong></td>
              <td style={styles.labelCell}>Status:</td>
              <td style={styles.valueCell}><strong>{riskAssessment.status?.name || 'N/A'}</strong></td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Title:</td>
              <td style={styles.valueCell} colSpan="3">{riskAssessment.title || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Risk Category:</td>
              <td style={styles.valueCell}>{riskAssessment.riskCategory?.name || 'N/A'}</td>
              <td style={styles.labelCell}>Created By:</td>
              <td style={styles.valueCell}>{riskAssessment.createdBy?.userDisplayName || riskAssessment.createdBy?.name || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Created Date:</td>
              <td style={styles.valueCell}>{formatDate(riskAssessment.createdAt)}</td>
              <td style={styles.labelCell}>Updated Date:</td>
              <td style={styles.valueCell}>{formatDate(riskAssessment.updatedAt)}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Timeline & Dates Section */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>2. TIMELINE & DATES</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Requested Date:</td>
              <td style={styles.valueCell}>{formatDateOnly(riskAssessment.requestedDate)}</td>
              <td style={styles.labelCell}>Received Date:</td>
              <td style={styles.valueCell}>{formatDateOnly(riskAssessment.receivedDate)}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Closed Date:</td>
              <td style={styles.valueCell}>{formatDate(riskAssessment.closedAt)}</td>
              <td style={styles.labelCell}></td>
              <td style={styles.valueCell}></td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Risk Event Description Section */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>3. RISK EVENT DESCRIPTION</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Parameter (Risk Event Description):</td>
              <td style={styles.valueCell} colSpan="3">{riskAssessment.parameter || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Effect (Consequence):</td>
              <td style={styles.valueCell} colSpan="3">{riskAssessment.effect || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Impact:</td>
              <td style={styles.valueCell}>{riskAssessment.impact || 'N/A'}</td>
              <td style={styles.labelCell}>Level of Risk:</td>
              <td style={styles.valueCell}>{riskAssessment.levelOfRisk || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Rationale (Explanation):</td>
              <td style={styles.valueCell} colSpan="3">{riskAssessment.rationale || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Result:</td>
              <td style={styles.valueCell}>{riskAssessment.result || 'N/A'}</td>
              <td style={styles.labelCell}></td>
              <td style={styles.valueCell}></td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Responsible & Departments Section */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>4. RESPONSIBLE & DEPARTMENTS</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Responsible For Action Type:</td>
              <td style={styles.valueCell}>{riskAssessment.responsibleForActionType || 'N/A'}</td>
              <td style={styles.labelCell}></td>
              <td style={styles.valueCell}></td>
            </tr>
            {riskAssessment.responsibleForActionType === 'USER' && (
              <tr>
                <td style={styles.labelCell}>Responsible Users:</td>
                <td style={styles.valueCell} colSpan="3">{formatArray(riskAssessment.responsibleForActionUsers)}</td>
              </tr>
            )}
            {riskAssessment.responsibleForActionType === 'ROLE' && (
              <tr>
                <td style={styles.labelCell}>Responsible Roles:</td>
                <td style={styles.valueCell} colSpan="3">{formatArray(riskAssessment.responsibleForActionRoles)}</td>
              </tr>
            )}
            {riskAssessment.responsibleForActionType === 'DEPARTMENT' && (
              <tr>
                <td style={styles.labelCell}>Responsible Departments:</td>
                <td style={styles.valueCell} colSpan="3">{formatArray(riskAssessment.responsibleForActionDepartments)}</td>
              </tr>
            )}
            <tr>
              <td style={styles.labelCell}>Involved Departments:</td>
              <td style={styles.valueCell} colSpan="3">{formatArray(riskAssessment.involvedDepartments)}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Actions Section */}
      {riskAssessment.riskAssessmentActions && riskAssessment.riskAssessmentActions.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>5. ACTIONS</h2>
          <table style={styles.fullTable}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Description</th>
                <th style={styles.tableHeader}>Started</th>
                <th style={styles.tableHeader}>Due Date</th>
                <th style={styles.tableHeader}>Completed</th>
              </tr>
            </thead>
            <tbody>
              {riskAssessment.riskAssessmentActions.map((action, index) => (
                <tr key={index}>
                  <td style={styles.tableCell}>{action.description || 'N/A'}</td>
                  <td style={styles.tableCell}>{formatDate(action.startedAt)}</td>
                  <td style={styles.tableCell}>{formatDateOnly(action.dueDate)}</td>
                  <td style={styles.tableCell}>{formatDate(action.closedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Timeline Section */}
      {riskAssessment.riskAssessmentTimeLineEntries && riskAssessment.riskAssessmentTimeLineEntries.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>6. TIMELINE</h2>
          <table style={styles.fullTable}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Date</th>
                <th style={styles.tableHeader}>Action</th>
                <th style={styles.tableHeader}>User</th>
                <th style={styles.tableHeader}>Comment</th>
              </tr>
            </thead>
            <tbody>
              {riskAssessment.riskAssessmentTimeLineEntries.map((entry, index) => (
                <tr key={index}>
                  <td style={styles.tableCell}>{formatDate(entry.performedAt)}</td>
                  <td style={styles.tableCell}>{entry.action || 'N/A'}</td>
                  <td style={styles.tableCell}>{entry.user?.userDisplayName || entry.user?.name || 'N/A'}</td>
                  <td style={styles.tableCell}>{entry.comment || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Approval Request Status Section */}
      {riskAssessment.approvalRequests && riskAssessment.approvalRequests.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>7. REVIEW AND APPROVAL STATUS</h2>
          {riskAssessment.approvalRequests.map((approval, idx) => (
            <div key={idx} style={{ marginBottom: idx < riskAssessment.approvalRequests.length - 1 ? '20px' : '0' }}>
              <h3 style={styles.subsectionTitle}>Approval Request {idx + 1}: {approval.code || approval.id || 'N/A'}</h3>
              <table style={styles.table}>
                <tbody>
                  <tr>
                    <td style={styles.labelCell}>Status:</td>
                    <td style={styles.valueCell}>{approval.status?.name || 'N/A'}</td>
                    <td style={styles.labelCell}>Priority:</td>
                    <td style={styles.valueCell}>{approval.priority?.name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td style={styles.labelCell}>Requested By:</td>
                    <td style={styles.valueCell}>{approval.requestedBy?.userDisplayName || approval.requestedBy?.name || 'N/A'}</td>
                    <td style={styles.labelCell}>Requested Date:</td>
                    <td style={styles.valueCell}>{formatDate(approval.requestedAt)}</td>
                  </tr>
                  <tr>
                    <td style={styles.labelCell}>Due Date:</td>
                    <td style={styles.valueCell}>{formatDateOnly(approval.dueDate)}</td>
                    <td style={styles.labelCell}>Completed Date:</td>
                    <td style={styles.valueCell}>{formatDate(approval.completedAt)}</td>
                  </tr>
                  {approval.description && (
                    <tr>
                      <td style={styles.labelCell}>Description:</td>
                      <td style={styles.valueCell} colSpan="3">{approval.description}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {approval.steps && approval.steps.length > 0 && (
                <div style={{ marginTop: '15px' }}>
                  <h4 style={styles.stepTitle}>Approval Steps:</h4>
                  <table style={styles.fullTable}>
                    <thead>
                      <tr>
                        <th style={styles.tableHeader}>Step</th>
                        <th style={styles.tableHeader}>Status</th>
                        <th style={styles.tableHeader}>Assigned To</th>
                        <th style={styles.tableHeader}>Reviewed By</th>
                        <th style={styles.tableHeader}>Approved Date</th>
                        <th style={styles.tableHeader}>Comments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approval.steps.map((step, stepIdx) => (
                        <tr key={stepIdx}>
                          <td style={styles.tableCell}>{step.stepNumber || stepIdx + 1}</td>
                          <td style={styles.tableCell}>{step.status?.name || 'N/A'}</td>
                          <td style={styles.tableCell}>{step.assignedApprover?.userDisplayName || step.assignedApprover?.name || 'N/A'}</td>
                          <td style={styles.tableCell}>
                            {step.approvedBy?.userDisplayName || step.approvedBy?.name || 
                             step.rejectedBy?.userDisplayName || step.rejectedBy?.name || 
                             step.reviewedBy?.userDisplayName || step.reviewedBy?.name || 'N/A'}
                          </td>
                          <td style={styles.tableCell}>
                            {formatDate(step.approvedAt || step.completedAt || step.rejectedAt || step.reviewedAt)}
                          </td>
                          <td style={styles.tableCell}>{step.comments || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {/* File Attachments Section */}
      {riskAssessment.fileAttachments && riskAssessment.fileAttachments.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            {riskAssessment.approvalRequests && riskAssessment.approvalRequests.length > 0 ? '8. FILE ATTACHMENTS' : '7. FILE ATTACHMENTS'}
          </h2>
          <table style={styles.fullTable}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>File Name</th>
                <th style={styles.tableHeader}>File Type</th>
                <th style={styles.tableHeader}>File Size</th>
                <th style={styles.tableHeader}>Uploaded Date</th>
                <th style={styles.tableHeader}>Uploaded By</th>
              </tr>
            </thead>
            <tbody>
              {riskAssessment.fileAttachments.map((file, index) => (
                <tr key={index}>
                  <td style={styles.tableCell}>{normalizeFileName(file.fileName || 'N/A')}</td>
                  <td style={styles.tableCell}>{file.fileType || 'N/A'}</td>
                  <td style={styles.tableCell}>
                    {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                  </td>
                  <td style={styles.tableCell}>{formatDate(file.uploadedAt)}</td>
                  <td style={styles.tableCell}>{file.uploadedBy?.userDisplayName || file.uploadedBy?.name || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Related Items Section */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>
          {riskAssessment.approvalRequests && riskAssessment.approvalRequests.length > 0
            ? (riskAssessment.fileAttachments && riskAssessment.fileAttachments.length > 0 ? '9. RELATED ITEMS' : '8. RELATED ITEMS')
            : (riskAssessment.fileAttachments && riskAssessment.fileAttachments.length > 0 ? '8. RELATED ITEMS' : '7. RELATED ITEMS')}
        </h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Related Complaints:</td>
              <td style={styles.valueCell} colSpan="3">
                {riskAssessment.complaints && riskAssessment.complaints.length > 0
                  ? riskAssessment.complaints.map(c => c.code || c.title || 'N/A').join(', ')
                  : 'N/A'}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Footer */}
      <div style={styles.footer}>
        <div style={styles.footerContent}>
          <p style={styles.footerText}>
            This report was generated on {dayjs().format('DD/MM/YYYY HH:mm')} from the Quality Management System.
          </p>
          <p style={styles.footerText}>
            Risk Assessment Code: <strong>{riskAssessment.code}</strong> | ID: {riskAssessment.id}
          </p>
        </div>
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    maxWidth: '210mm',
    margin: '0 auto',
    padding: '20mm',
    backgroundColor: '#fff',
    fontFamily: 'Arial, sans-serif',
    fontSize: '11pt',
    lineHeight: '1.4',
    color: '#000',
  },
  header: {
    borderBottom: '3px solid #000',
    paddingBottom: '10px',
    marginBottom: '20px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'flex-start',
  },
  headerRight: {
    flex: '1 1 auto',
    textAlign: 'right',
  },
  logo: {
    maxHeight: '50px',
    maxWidth: '200px',
    height: 'auto',
    width: 'auto',
    objectFit: 'contain',
    marginRight: '20px',
  },
  title: {
    fontSize: '24pt',
    fontWeight: 'bold',
    margin: 0,
    color: '#000',
    marginBottom: '5px',
  },
  referenceDocument: {
    fontSize: '11pt',
    margin: 0,
    color: '#000',
    textAlign: 'right',
  },
  referenceDocumentMuted: {
    fontStyle: 'italic',
    color: '#666',
  },
  section: {
    marginBottom: '20px',
    pageBreakInside: 'auto',
  },
  sectionTitle: {
    fontSize: '14pt',
    fontWeight: 'bold',
    marginBottom: '10px',
    paddingBottom: '5px',
    borderBottom: '2px solid #000',
    color: '#000',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '10px',
  },
  fullTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '10px',
    border: '1px solid #000',
  },
  labelCell: {
    width: '25%',
    padding: '8px',
    fontWeight: 'bold',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    verticalAlign: 'top',
  },
  valueCell: {
    width: '25%',
    padding: '8px',
    border: '1px solid #ddd',
    verticalAlign: 'top',
  },
  tableHeader: {
    padding: '10px',
    backgroundColor: '#333',
    color: '#fff',
    fontWeight: 'bold',
    textAlign: 'left',
    border: '1px solid #000',
  },
  tableCell: {
    padding: '8px',
    border: '1px solid #ddd',
    verticalAlign: 'top',
  },
  footer: {
    marginTop: '30px',
    paddingTop: '10px',
    borderTop: '2px solid #000',
    textAlign: 'center',
    fontSize: '9pt',
    color: '#666',
  },
  footerContent: {
    marginTop: '10px',
  },
  footerText: {
    margin: '5px 0',
  },
  subsectionTitle: {
    fontSize: '12pt',
    fontWeight: 'bold',
    marginTop: '15px',
    marginBottom: '10px',
    color: '#000',
  },
  stepTitle: {
    fontSize: '11pt',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#333',
  },
};

// Print-specific styles
const printStyles = `
  @media print {
    @page {
      size: A4;
      margin: 15mm;
    }
    
    /* Hide print controls and navigation */
    .no-print {
      display: none !important;
    }
    
    /* Hide any headers, sidebars, navigation from MainLayout */
    header, nav, aside, .sidebar, .main-header, .app-header,
    [class*="header"]:not(.risk-assessment-report), [class*="nav"], [class*="sidebar"] {
      display: none !important;
    }
    
    /* Ensure report wrapper is visible */
    .risk-assessment-report-wrapper {
      display: block !important;
      visibility: visible !important;
      width: 100% !important;
      height: auto !important;
    }
    
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      width: 100% !important;
    }
    
    /* Ensure print modal and its content are fully visible and printable */
    .print-modal {
      position: static !important;
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      display: block !important;
      width: 100% !important;
      top: auto !important;
      left: auto !important;
      right: auto !important;
      bottom: auto !important;
      inset: auto !important;
    }
    
    .print-modal > div {
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      position: relative !important;
    }
    
    /* Remove any height restrictions from all containers when printing */
    * {
      max-height: none !important;
    }
    
    /* Ensure all divs can expand to show full content */
    div {
      height: auto !important;
      min-height: auto !important;
      overflow: visible !important;
    }
    
    /* Hide everything except the print container */
    body > *:not(#print-container) {
      display: none !important;
    }
    
    /* Ensure the print container is visible */
    #print-container {
      display: block !important;
      visibility: visible !important;
      position: static !important;
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
    }
    
    .risk-assessment-report-wrapper {
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
      height: auto !important;
      overflow: visible !important;
    }
    
    .risk-assessment-report {
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 20mm !important;
      box-shadow: none !important;
      height: auto !important;
      overflow: visible !important;
      page-break-after: auto;
      position: relative !important;
      background: white !important;
      display: block !important;
      visibility: visible !important;
    }
    
    .no-print {
      display: none !important;
    }
    
    .print-only {
      display: block !important;
    }
    
    /* Allow sections to break across pages */
    section {
      page-break-inside: auto;
      page-break-after: auto;
      margin-bottom: 15px;
      overflow: visible !important;
    }
    
    /* Allow tables to break across pages */
    table {
      page-break-inside: auto;
      border-collapse: collapse;
      width: 100% !important;
    }
    
    /* Keep table headers on each page */
    thead {
      display: table-header-group;
    }
    
    tbody {
      display: table-row-group;
    }
    
    tr {
      page-break-inside: auto;
      page-break-after: auto;
    }
    
    /* Try to keep section titles with their content */
    h2 {
      page-break-after: avoid;
      page-break-inside: avoid;
    }
    
    /* Allow cells to break if needed */
    td, th {
      page-break-inside: auto;
    }
    
    /* Prevent orphaned content */
    p, div {
      orphans: 3;
      widows: 3;
    }
    
    /* Ensure all content is visible for printing */
    * {
      visibility: visible !important;
    }
  }
  
  @media screen {
    .risk-assessment-report {
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
      margin: 20px auto;
    }
  }
`;

export default RiskAssessmentReport;

